import React, { useState, useEffect, useRef, useId } from 'react';
import { Pause, Play, X } from 'lucide-react';
import './Pomodoro.css';

export const POMODORO_MINUTES = 25;

// Convert an estimated duration into 25-minute pomodoro sessions.
// e.g. 40 min -> [25, 25], 90 min -> [25, 25, 25, 25]
export function pomodoroDurations(minutes) {
  const mins = parseInt(minutes, 10);
  if (!Number.isFinite(mins) || mins <= 0) return [];
  const count = Math.max(1, Math.ceil(mins / POMODORO_MINUTES));
  return Array.from({ length: count }, () => POMODORO_MINUTES);
}

export function localDateKey() {
  return new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
}

export function pomodorosDone(todo) {
  return (todo.pomodoro_progress || {})[localDateKey()] || 0;
}

// Bite order: around the rim clockwise from the top-right, then the middle.
const BITES = [
  { x: 126.7, y: 61.7, r: 28 },
  { x: 167.5, y: 90.0, r: 28 },
  { x: 176.8, y: 132.8, r: 28 },
  { x: 150.1, y: 170.5, r: 28 },
  { x: 100.0, y: 186.0, r: 28 },
  { x: 49.9, y: 170.5, r: 28 },
  { x: 23.2, y: 132.8, r: 28 },
  { x: 32.5, y: 90.0, r: 28 },
  { x: 73.3, y: 61.7, r: 28 },
  { x: 140, y: 104, r: 32 },
  { x: 60, y: 104, r: 32 },
  { x: 135, y: 148, r: 34 },
  { x: 65, y: 148, r: 34 },
  { x: 100, y: 165, r: 34 },
  { x: 100, y: 96, r: 36 },
  { x: 100, y: 124, r: 52 },
];

const easeOut = (t) => 1 - (1 - t) * (1 - t);

// A tomato that gets progressively eaten. eaten: 0 (whole) -> 1 (only the stem left).
export const Tomato = ({ eaten = 0, size = 24, className = '' }) => {
  const id = useId();
  const clamped = Math.min(1, Math.max(0, eaten));
  const progress = clamped * BITES.length;
  const fullBites = Math.floor(progress);
  const partial = progress - fullBites;

  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={`tomato-svg ${className}`}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`${id}-body`} cx="35%" cy="30%" r="85%">
          <stop offset="0%" stopColor="#E8604C" />
          <stop offset="55%" stopColor="#D93A2B" />
          <stop offset="100%" stopColor="#A82A1C" />
        </radialGradient>
        <mask id={`${id}-bites`}>
          <rect width="200" height="200" fill="white" />
          {BITES.slice(0, fullBites).map((b, i) => (
            <circle key={i} cx={b.x} cy={b.y} r={b.r} fill="black" />
          ))}
          {fullBites < BITES.length && partial > 0 && (
            <circle
              cx={BITES[fullBites].x}
              cy={BITES[fullBites].y}
              r={BITES[fullBites].r * easeOut(partial)}
              fill="black"
            />
          )}
        </mask>
      </defs>
      {clamped >= 1 && (
        <ellipse
          cx="100"
          cy="126"
          rx="70"
          ry="56"
          fill="none"
          stroke="#C9A962"
          strokeWidth="7"
          strokeDasharray="12 16"
          strokeLinecap="round"
          opacity="0.7"
        />
      )}
      {clamped < 1 && (
        <g mask={`url(#${id}-bites)`}>
          <ellipse cx="100" cy="126" rx="76" ry="62" fill={`url(#${id}-body)`} />
          <ellipse
            cx="72"
            cy="102"
            rx="22"
            ry="13"
            fill="rgba(255,255,255,0.3)"
            transform="rotate(-20 72 102)"
          />
        </g>
      )}
      {/* Stem and sepal stay uneaten, like an apple core */}
      <path
        d="M100 68
           C 96 60, 88 56, 76 58
           C 82 50, 92 48, 98 52
           C 96 42, 100 34, 108 30
           C 106 38, 107 46, 104 52
           C 112 46, 122 46, 130 52
           C 120 54, 112 58, 108 64
           C 116 64, 122 68, 126 76
           C 116 72, 106 72, 100 78
           C 94 72, 84 72, 74 76
           C 78 68, 86 64, 94 64
           C 96 65, 98 66, 100 68 Z"
        fill="#3E7C4F"
      />
      <path d="M98 52 C 96 42, 100 34, 108 30 C 106 38, 107 46, 103 53 Z" fill="#2C4A3E" />
    </svg>
  );
};

// Optional dev override: append ?pomo=SECONDS to the URL to shorten sessions.
function sessionSeconds(minutes) {
  const v = parseInt(new URLSearchParams(window.location.search).get('pomo'), 10);
  return Number.isFinite(v) && v > 0 ? v : minutes * 60;
}

export const PomodoroTimer = ({ task, minutes, index, total, onComplete, onClose, compact = false }) => {
  const [duration] = useState(() => sessionSeconds(minutes));
  const [remaining, setRemaining] = useState(duration);
  const [paused, setPaused] = useState(false);
  const [finished, setFinished] = useState(false);
  const endRef = useRef(Date.now() + duration * 1000);
  const completedRef = useRef(false);

  useEffect(() => {
    if (paused || finished) return;
    const tick = setInterval(() => {
      const left = Math.max(0, (endRef.current - Date.now()) / 1000);
      setRemaining(left);
      if (left <= 0) setFinished(true);
    }, 200);
    return () => clearInterval(tick);
  }, [paused, finished]);

  useEffect(() => {
    if (!finished) return;
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
    const t = setTimeout(onClose, 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const togglePause = () => {
    if (paused) endRef.current = Date.now() + remaining * 1000;
    setPaused((p) => !p);
  };

  const progress = finished ? 1 : 1 - remaining / duration;
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(Math.floor(remaining % 60)).padStart(2, '0');

  if (compact) return (
    <div className="focus-timer" aria-label={`Pomodoro for ${task.title}`}>
      <Tomato eaten={progress} size={28} />
      <span className="focus-timer-time" role="timer">{finished ? 'Session complete' : `${mm}:${ss}`}</span>
      {!finished && <button onClick={togglePause} aria-label={paused ? 'Resume timer' : 'Pause timer'}>
        {paused ? <Play size={16} /> : <Pause size={16} />}
      </button>}
      <button onClick={onClose} aria-label="Stop timer"><X size={16} /></button>
    </div>
  );

  return (
    <div className="pomodoro-overlay">
      <div className="pomodoro-card">
        <button className="pomodoro-close" onClick={onClose} title="Close">
          <X size={18} />
        </button>
        <h3 className="pomodoro-task-title">{task.title}</h3>
        <p className="pomodoro-session-label">
          Pomodoro {index + 1} of {total} &middot; {minutes} min
        </p>
        <div className={`pomodoro-stage ${paused ? 'paused' : ''}`}>
          <Tomato eaten={progress} size={240} />
        </div>
        <div className="pomodoro-time">{finished ? 'Eaten!' : `${mm}:${ss}`}</div>
        {finished ? (
          <p className="pomodoro-done-msg">Pomodoro complete 🍅</p>
        ) : (
          <div className="pomodoro-controls">
            <button className="pomodoro-ctrl-btn primary" onClick={togglePause}>
              {paused ? <Play size={16} /> : <Pause size={16} />}
              {paused ? 'Resume' : 'Pause'}
            </button>
            <button className="pomodoro-ctrl-btn" onClick={onClose}>
              <X size={16} />
              Give up
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const PomodoroStrip = ({ todo, onStart }) => {
  const durations = pomodoroDurations(todo.estimated_minutes);
  if (durations.length === 0) return null;
  const done = pomodorosDone(todo);

  return (
    <div className="pomodoro-strip">
      {durations.map((min, i) => {
        const isEaten = i < done;
        return (
          <button
            key={i}
            className={`pomodoro-btn ${isEaten ? 'eaten' : ''}`}
            disabled={isEaten || todo.completed}
            title={
              isEaten
                ? `Pomodoro ${i + 1} · eaten`
                : `Pomodoro ${i + 1} · ${min} min — click to start`
            }
            onClick={() => onStart(todo, i, min, durations.length)}
          >
            <Tomato eaten={isEaten ? 1 : 0} size={22} />
          </button>
        );
      })}
    </div>
  );
};
